import { useTranslation } from "@fmc-digital/i18n";
import { Text } from "@fmc-digital/ui";
import {
  processEmailForDisplay,
  formatPhoneForDisplay,
  addEmailLineBreakOpportunity,
} from "@/utils/mfa-formatting";

import type { MFADestinationTextProps } from "./mfa-destination-text.types";

/**
 * MFADestinationText Molecule
 * Displays formatted destination text for where the code was sent.
 * Prefix on the first line; destination in brackets on the second (matches Figma).
 * Phone and masked email stay on one line via nowrap.
 */
export function MFADestinationText({
  factorType,
  profile,
}: Readonly<MFADestinationTextProps>) {
  const { t } = useTranslation();

  const getDestinationText = () => {
    if (factorType === "sms" && profile.phoneNumber) {
      return formatPhoneForDisplay(profile.phoneNumber);
    }
    if (factorType === "email" && profile.email) {
      const maskedEmail = processEmailForDisplay(profile.email);
      return addEmailLineBreakOpportunity(maskedEmail);
    }
    // Fallback
    return factorType === "sms"
      ? t("auth.mfa.smsOption")
      : t("auth.mfa.emailOption");
  };

  const destination = getDestinationText();
  const isEmail = factorType === "email";
  const destinationClassName = isEmail
    ? "mfa-destination-email"
    : "mfa-destination-nowrap";

  return (
    <Text
      variant="headlineSmall"
      className="mfa-title mb-4 md:mb-[22px]"
      align="center"
      as="h1"
    >
      <span className="mfa-destination-prefix">{t("auth.mfa.subtitlePrefix")}</span>
      <span className={destinationClassName}>[{destination}]</span>
    </Text>
  );
}
